import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { normalizeRole } from '@/lib/hub-authorization';

const ALLOWED_ROLES: ReadonlySet<string> = new Set(['system-admin', 'settings-admin', 'operator-admin']);

function isOperatorAdmin(role: string | null): boolean {
  if (role == null) return false;
  return ALLOWED_ROLES.has(normalizeRole(role));
}

interface OperatorOnlyProtectedRouteProps {
  children: ReactNode;
}

/**
 * Protects routes so only operator-admin (and system-admin) can access.
 * Use for Support and other operator-admin-only pages.
 */
export function OperatorOnlyProtectedRoute({ children }: OperatorOnlyProtectedRouteProps) {
  const { role, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return <Loader text="Checking access..." />;
  }

  if (!isOperatorAdmin(role ?? null)) {
    return (
      <div className="flex h-(--page-main-height) items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="error">
                <Lock />
              </EmptyMedia>
              <EmptyTitle>Access denied</EmptyTitle>
              <EmptyDescription>This page is only available to operator-admin users.</EmptyDescription>
              <Button variant="default" className="mt-4" onClick={() => navigate('/apps')}>
                Back to apps
              </Button>
            </EmptyHeader>
          </Empty>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
